import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Bell, Plus, Send, Trash2, Pencil, Clock, CheckCircle2, ShoppingBag, Users, Settings } from 'lucide-react';
import axios from 'axios';

const API = `${window.location.origin}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const statusCfg = {
  draft: { label: 'Bozza', color: 'var(--text-muted)', bg: 'rgba(148,163,184,0.1)' },
  scheduled: { label: 'Programmata', color: '#3B82F6', bg: 'rgba(59,130,246,0.1)' },
  sent: { label: 'Inviata', color: '#10B981', bg: 'rgba(16,185,129,0.1)' },
};

export default function AdminPushNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', body: '', url: '/', scheduled_at: '', target: 'all' });
  const [saving, setSaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});

  const load = async () => {
    try {
      const [n, s] = await Promise.all([
        api.get('/admin/push/notifications'),
        api.get('/admin/push/settings')
      ]);
      setNotifications(n.data);
      setSettings(s.data);
      setSettingsForm(s.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openNew = () => {
    setEditingId(null);
    setForm({ title: '', body: '', url: '/', scheduled_at: '', target: 'all' });
    setDialogOpen(true);
  };

  const openEdit = (n) => {
    setEditingId(n.notification_id);
    setForm({
      title: n.title, body: n.body, url: n.url || '/',
      scheduled_at: n.scheduled_at ? n.scheduled_at.slice(0, 16) : '',
      target: n.target || 'all'
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title) { toast.error('Il titolo e\' obbligatorio'); return; }
    setSaving(true);
    try {
      const data = { ...form };
      if (data.scheduled_at) {
        data.scheduled_at = new Date(data.scheduled_at).toISOString();
        data.status = 'scheduled';
      }
      if (editingId) {
        await api.put(`/admin/push/notifications/${editingId}`, data);
        toast.success('Notifica aggiornata');
      } else {
        await api.post('/admin/push/notifications', data);
        toast.success('Notifica creata');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
    setSaving(false);
  };

  const handleSend = async (nid) => {
    if (!window.confirm('Inviare questa notifica adesso?')) return;
    try {
      const res = await api.post(`/admin/push/send/${nid}`);
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'invio');
    }
  };

  const handleDelete = async (nid) => {
    if (!window.confirm('Eliminare questa notifica?')) return;
    try {
      await api.delete(`/admin/push/notifications/${nid}`);
      toast.success('Eliminata');
      load();
    } catch {}
  };

  const handleTestPush = async () => {
    try {
      const res = await api.post('/admin/push/test', { title: 'Test Quintessenza', body: 'Notifica di test dal pannello admin!' });
      toast.success(`Test inviato a ${res.data.sent} dispositivi`);
    } catch (err) {
      toast.error('Errore nel test');
    }
  };

  const saveSettings = async () => {
    try {
      await api.put('/admin/push/settings', settingsForm);
      toast.success('Impostazioni salvate');
      setSettingsOpen(false);
      load();
    } catch { toast.error('Errore'); }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-push">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl tracking-tight" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>Notifiche Push</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Crea, programma e invia notifiche push</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} className="rounded-sm text-xs" data-testid="push-settings-btn">
            <Settings className="w-3.5 h-3.5 mr-1" /> Carrello Abbandonato
          </Button>
          <Button onClick={openNew} data-testid="create-push-btn"
            className="rounded-sm text-sm" style={{ backgroundColor: 'var(--accent)', color: '#fff' }}>
            <Plus className="w-4 h-4 mr-1" /> Nuova Notifica
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }}>
          <CardContent className="p-4 text-center">
            <p className="text-xl font-medium" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{settings.total_subscribers || 0}</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Iscritti Push</p>
          </CardContent>
        </Card>
        <Card className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }}>
          <CardContent className="p-4 text-center">
            <p className="text-xl font-medium" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{notifications.filter(n => n.status === 'scheduled').length}</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Programmate</p>
          </CardContent>
        </Card>
        <Card className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }}>
          <CardContent className="p-4 text-center">
            <p className="text-xl font-medium" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{notifications.filter(n => n.status === 'sent').reduce((s, n) => s + (n.sent_count || 0), 0)}</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Inviate Totali</p>
          </CardContent>
        </Card>
      </div>

      {/* Test + Abandoned Cart status */}
      <div className="flex gap-3">
        <Button variant="outline" size="sm" onClick={handleTestPush} className="rounded-sm text-xs" data-testid="test-push-btn">
          <Bell className="w-3.5 h-3.5 mr-1" /> Invia Test Push
        </Button>
        <div className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-sm" style={{ backgroundColor: 'var(--bg-warm)', color: 'var(--text-secondary)' }}>
          <ShoppingBag className="w-3.5 h-3.5" />
          Carrello abbandonato: <strong>{settings.abandoned_cart_enabled !== false ? `${settings.abandoned_cart_delay_minutes || 30} min` : 'Disattivato'}</strong>
        </div>
      </div>

      {/* Notifications list */}
      <Card className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="push-notifications-list">
        <CardContent className="p-0">
          <div className="divide-y" style={{ borderColor: 'var(--border-light)' }}>
            {notifications.map(n => {
              const sc = statusCfg[n.status] || statusCfg.draft;
              return (
                <div key={n.notification_id} className="p-4 flex items-start gap-4" data-testid={`notif-row-${n.notification_id}`}>
                  <div className="w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: sc.bg }}>
                    <Bell className="w-4 h-4" style={{ color: sc.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{n.title}</p>
                      <Badge className="text-xs rounded-sm flex-shrink-0" style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</Badge>
                      {n.target === 'cart_abandoned' && (
                        <Badge className="text-xs rounded-sm flex-shrink-0" variant="outline"><ShoppingBag className="w-3 h-3 mr-0.5" /> Carrello</Badge>
                      )}
                    </div>
                    <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{n.body}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                      {n.scheduled_at && <span><Clock className="w-3 h-3 inline mr-0.5" />{new Date(n.scheduled_at).toLocaleString('it-IT')}</span>}
                      {n.sent_at && <span><CheckCircle2 className="w-3 h-3 inline mr-0.5" />Inviata: {new Date(n.sent_at).toLocaleString('it-IT')}</span>}
                      {n.sent_count > 0 && <span><Users className="w-3 h-3 inline mr-0.5" />{n.sent_count} destinatari</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {n.status !== 'sent' && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => handleSend(n.notification_id)} data-testid={`send-notif-${n.notification_id}`}>
                          <Send className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(n)}>
                          <Pencil className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
                        </Button>
                      </>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(n.notification_id)}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  </div>
                </div>
              );
            })}
            {notifications.length === 0 && (
              <div className="text-center py-12">
                <Bell className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Nessuna notifica creata</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="push-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: 'var(--font-heading)' }}>
              {editingId ? 'Modifica Notifica' : 'Nuova Notifica Push'}
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Crea o programma una notifica push per i tuoi iscritti
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs">Titolo</Label>
              <Input value={form.title} onChange={e => setField('title', e.target.value)}
                placeholder="es. Nuova offerta disponibile!" className="mt-1 text-sm" data-testid="push-title" />
            </div>
            <div>
              <Label className="text-xs">Messaggio</Label>
              <Input value={form.body} onChange={e => setField('body', e.target.value)}
                placeholder="es. Scopri lo sconto esclusivo della settimana" className="mt-1 text-sm" data-testid="push-body" />
            </div>
            <div>
              <Label className="text-xs">URL di destinazione</Label>
              <Input value={form.url} onChange={e => setField('url', e.target.value)}
                placeholder="/" className="mt-1 text-sm" data-testid="push-url" />
            </div>
            <div>
              <Label className="text-xs">Programmazione (vuoto = bozza)</Label>
              <Input type="datetime-local" value={form.scheduled_at} onChange={e => setField('scheduled_at', e.target.value)}
                className="mt-1 text-sm" data-testid="push-scheduled" />
            </div>
            <Button onClick={handleSave} disabled={saving} data-testid="save-push-btn"
              className="w-full rounded-sm" style={{ backgroundColor: 'var(--accent)', color: '#fff' }}>
              {saving ? 'Salvataggio...' : editingId ? 'Aggiorna' : form.scheduled_at ? 'Programma Invio' : 'Salva come Bozza'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Abandoned Cart Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-md" data-testid="cart-settings-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: 'var(--font-heading)' }}>Carrello Abbandonato</DialogTitle>
            <DialogDescription className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Configura la notifica automatica per chi abbandona il carrello
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex items-center gap-3 py-1">
              <Switch checked={settingsForm.abandoned_cart_enabled !== false}
                onCheckedChange={v => setSettingsForm(p => ({ ...p, abandoned_cart_enabled: v }))}
                data-testid="cart-enabled-switch" />
              <Label className="text-sm">Notifica attiva</Label>
            </div>
            <div>
              <Label className="text-xs">Ritardo invio (minuti dopo l'abbandono)</Label>
              <Input type="number" min={1} value={settingsForm.abandoned_cart_delay_minutes || 30}
                onChange={e => setSettingsForm(p => ({ ...p, abandoned_cart_delay_minutes: parseInt(e.target.value) || 30 }))}
                className="mt-1 text-sm" data-testid="cart-delay-input" />
            </div>
            <div>
              <Label className="text-xs">Titolo notifica</Label>
              <Input value={settingsForm.abandoned_cart_title || ''}
                onChange={e => setSettingsForm(p => ({ ...p, abandoned_cart_title: e.target.value }))}
                className="mt-1 text-sm" data-testid="cart-title-input" />
            </div>
            <div>
              <Label className="text-xs">Messaggio notifica</Label>
              <Input value={settingsForm.abandoned_cart_body || ''}
                onChange={e => setSettingsForm(p => ({ ...p, abandoned_cart_body: e.target.value }))}
                className="mt-1 text-sm" data-testid="cart-body-input" />
            </div>
            <Button onClick={saveSettings} data-testid="save-cart-settings-btn"
              className="w-full rounded-sm" style={{ backgroundColor: 'var(--accent)', color: '#fff' }}>
              Salva Impostazioni
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
